import { Link } from 'react-router-dom';
import { Leaf, Cpu, HeartPulse, Truck, Wrench, Zap, Cloud, Users, Camera, Thermometer, MapPin, AlertTriangle, Activity, Smartphone, LayoutDashboard, Package, Gauge, Navigation, BarChart3, RefreshCw } from 'lucide-react';
import VehicleSlideshow from '../components/VehicleSlideshow';
import './HomePage.css';

const features = [
  { Icon: Leaf,       title: 'Zero Emission',     desc: 'Fully electric fleet reducing carbon footprint across the entire supply chain.' },
  { Icon: Cpu,        title: 'Smart Tech',         desc: 'Real-time IoT sensors, AI routing, and predictive maintenance for total reliability.' },
  { Icon: HeartPulse, title: 'Healthcare First',   desc: 'Specialized for temperature-sensitive medical and pharmaceutical deliveries.' },
];

const missionItems = [
  'Decarbonize the logistics industry',
  'Enhance supply chain visibility and transparency',
  'Deliver mission-critical goods with extreme reliability',
  'Create a highly scalable EV logistics ecosystem',
  'Provide cost parity through superior operational technology',
];

const onTruck = [
  { Icon: Camera,        label: 'AI CCTV Monitoring' },
  { Icon: Thermometer,   label: 'Temp & Humidity Sensors' },
  { Icon: MapPin,        label: 'GPS & Telematics' },
  { Icon: AlertTriangle, label: 'Alcohol Sensor Detection' },
  { Icon: Activity,      label: 'Driver Behavior Analytics' },
  { Icon: Smartphone,    label: 'Smart Dashboard Hub' },
];

const offTruck = [
  { Icon: LayoutDashboard, label: 'Real-time Fleet Monitoring' },
  { Icon: Package,         label: 'ETA & Delivery Tracking' },
  { Icon: Gauge,           label: 'Compliance Verification' },
  { Icon: Navigation,      label: 'Dynamic Route Optimization' },
  { Icon: BarChart3,       label: 'Data Analytics Dashboard' },
  { Icon: RefreshCw,       label: 'Automated Dispatching' },
];

const ecoPartners = [
  { Icon: Truck,  title: 'Vehicle Partners',    desc: 'Top-tier EV Manufacturers' },
  { Icon: Wrench, title: 'Maintenance',         desc: 'Expert servicing networks (e.g. B-Quik)' },
  { Icon: Zap,    title: 'Charging Infra',      desc: 'Nationwide EV charging networks' },
  { Icon: Cloud,  title: 'Technology',          desc: 'Cloud, AI & IoT infrastructure providers' },
  { Icon: Users,  title: 'Industry Partners',   desc: 'Healthcare, Pharma, Food, FMCG clients' },
];

const phases = [
  { status: 'Active',    num: '01', title: 'Healthcare Logistics', desc: 'Deploying 4-wheel EV trucks specifically engineered for temperature-controlled medical and pharmaceutical delivery.' },
  { status: 'Upcoming',  num: '02', title: 'Urban Last Mile',      desc: 'Introducing nimble EV bikes and smaller vehicles optimized for dense urban environments and rapid localized delivery.' },
  { status: 'Future',    num: '03', title: 'Regional Transport',   desc: 'Scaling up to heavy-duty EV trucks for long-haul regional logistics, connecting cities with zero emissions.' },
];

const phaseColor = { Active: '#22c55e', Upcoming: '#f59e0b', Future: '#6366f1' };

export default function HomePage() {
  return (
    <div className="home">

      {/* ── HERO ── */}
      <section className="home-hero">
        <div className="home-hero-grid" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/hero-bg.png)` }} />
        <div className="home-hero-content">
          <p className="home-hero-kicker">The Future of Logistics is Here</p>
          <h1 className="home-hero-title">
            Next-Generation<br />
            <span className="home-hero-accent">EV Logistics</span> Platform
          </h1>
          <p className="home-hero-desc">
            Zero-emission, technology-driven transportation starting with the healthcare supply chain,
            engineered for unparalleled reliability and visibility.
          </p>
          <div className="home-hero-actions">
            <Link to="/ecosystem" className="home-btn-primary">Explore Platform</Link>
            <Link to="/contact"   className="home-btn-secondary">Contact Sales</Link>
          </div>
        </div>
      </section>

      {/* ── FEATURES ── */}
      <section className="home-features">
        <div className="home-features-header">
          <h2>Smarter Logistics, Cleaner Future</h2>
          <p>Axion Mobility integrates electric vehicle infrastructure with cutting-edge software to transform how mission-critical goods are delivered.</p>
        </div>
        <div className="home-features-grid">
          {features.map(({ Icon, title, desc }) => (
            <div key={title} className="home-feature-card">
              <div className="hf-icon"><Icon size={24} strokeWidth={1.7} /></div>
              <h3>{title}</h3>
              <p>{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── VEHICLE SLIDESHOW ── */}
      <VehicleSlideshow />

      {/* ── VISION ── */}
      <section className="home-vision">
        <div className="home-vision-left">
          <span className="home-eyebrow">Our Vision</span>
          <h2>One of the Best Logistics Networks in Thailand</h2>
          <p>Building a sustainable, transparent, and technology-driven ecosystem that powers the future of supply chains.</p>
          <Link to="/vision" className="home-btn-ghost">Learn More →</Link>
        </div>
        <div className="home-vision-right">
          <p className="home-mission-label">Mission Objectives</p>
          <ul className="home-mission-list">
            {missionItems.map((m, i) => (
              <li key={i}>
                <span className="home-mission-num">{String(i + 1).padStart(2, '0')}</span>
                <span>{m}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ── TECHNOLOGY ── */}
      <section className="home-tech">
        <div className="home-tech-bg" />
        <div className="home-tech-inner">
          <div className="home-tech-header">
            <span className="home-eyebrow">Technology</span>
            <h2>Driven By Technology</h2>
            <p>Our proprietary tech stack bridges physical vehicles with cloud intelligence, ensuring GDP, GDPMD, and ISO compliance for sensitive cargo.</p>
          </div>
          <div className="home-tech-cols">
            <div className="home-tech-col">
              <h3 className="home-tech-col-title">On-Truck Tech</h3>
              {onTruck.map(({ Icon, label }) => (
                <div key={label} className="home-tech-item">
                  <Icon size={16} strokeWidth={1.8} />{label}
                </div>
              ))}
            </div>
            <div className="home-tech-col">
              <h3 className="home-tech-col-title">Off-Truck Tech</h3>
              {offTruck.map(({ Icon, label }) => (
                <div key={label} className="home-tech-item">
                  <Icon size={16} strokeWidth={1.8} />{label}
                </div>
              ))}
            </div>
          </div>
          <div className="home-tech-cta">
            <Link to="/technology" className="home-btn-ghost">View Full Technology Stack →</Link>
          </div>
        </div>
      </section>

      {/* ── ECOSYSTEM ── */}
      <section className="home-eco">
        <span className="home-eyebrow">The Axion Ecosystem</span>
        <h2>A collaborative network of industry leaders working together to revolutionize the logistics landscape.</h2>
        <div className="home-eco-grid">
          {ecoPartners.map(({ Icon, title, desc }) => (
            <div key={title} className="home-eco-card">
              <div className="he-icon"><Icon size={22} strokeWidth={1.7} /></div>
              <h3>{title}</h3>
              <p>{desc}</p>
            </div>
          ))}
        </div>
        <Link to="/ecosystem" className="home-btn-ghost">Explore Ecosystem →</Link>
      </section>

      {/* ── PHASES ── */}
      <section className="home-phases">
        <span className="home-eyebrow">Strategic Expansion</span>
        <h2>Our multi-phase approach to dominating the sustainable logistics market.</h2>
        <div className="home-phases-grid">
          {phases.map(({ status, num, title, desc }) => (
            <div key={num} className="home-phase-card" style={{'--pc': phaseColor[status]}}>
              <div className="hp-top">
                <span className="hp-status" style={{color: phaseColor[status]}}>{status}</span>
                <span className="hp-num">{num}</span>
              </div>
              <p className="hp-phase">Phase {parseInt(num)}</p>
              <h3 className="hp-title">{title}</h3>
              <p className="hp-desc">{desc}</p>
            </div>
          ))}
        </div>
      </section>

    </div>
  );
}
